import { EventEmitter } from 'events';
import { WhatsAppClient } from '../client/WhatsAppClient';

export interface QueuedMessage {
  id: string;
  tel: string;
  chatId: string;
  content: any;
  type: 'text' | 'media' | 'interactive' | 'poll' | 'location' | 'voice' | 'sticker';
  priority: 'urgent' | 'high' | 'normal' | 'low';
  retryCount: number;
  maxRetries: number;
  createdAt: Date;
  scheduledAt?: Date;
  metadata?: Record<string, any>;
}

export interface QueueConfig {
  maxConcurrent: number;
  rateLimitPerMinute: number;
  retryAttempts: number;
  retryDelay: number;
  maxQueueSize: number;
  priorityLevels: string[];
  batchSize: number;
  processingInterval: number;
}

export interface QueueStats {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  retrying: number;
}

export interface RateLimiter {
  checkLimit(key: string): boolean;
  addRequest(key: string): void;
  getRemainingRequests(key: string): number;
  reset(key: string): void;
}

export class TokenBucketRateLimiter implements RateLimiter {
  private buckets: Map<string, { tokens: number; lastRefill: number }> = new Map();

  constructor(
    private maxTokens: number,
    private refillRate: number // tokens per minute
  ) {}

  checkLimit(key: string): boolean {
    const bucket = this.getBucket(key);
    this.refillBucket(bucket);
    return bucket.tokens > 0;
  }

  addRequest(key: string): void {
    const bucket = this.getBucket(key);
    this.refillBucket(bucket);
    if (bucket.tokens > 0) {
      bucket.tokens--;
    }
  }

  getRemainingRequests(key: string): number {
    const bucket = this.getBucket(key);
    this.refillBucket(bucket);
    return bucket.tokens;
  }

  reset(key: string): void {
    this.buckets.delete(key);
  }

  private getBucket(key: string): { tokens: number; lastRefill: number } {
    if (!this.buckets.has(key)) {
      this.buckets.set(key, {
        tokens: this.maxTokens,
        lastRefill: Date.now()
      });
    }
    return this.buckets.get(key)!;
  }

  private refillBucket(bucket: { tokens: number; lastRefill: number }): void {
    const now = Date.now();
    const timePassed = now - bucket.lastRefill;
    const tokensToAdd = Math.floor((timePassed / 60000) * this.refillRate);
    
    if (tokensToAdd > 0) {
      bucket.tokens = Math.min(this.maxTokens, bucket.tokens + tokensToAdd);
      bucket.lastRefill = now;
    }
  }
}

export class MessageQueue extends EventEmitter {
  private queue: QueuedMessage[] = [];
  private processing: Map<string, QueuedMessage> = new Map();
  private completed: Set<string> = new Set();
  private failed: Map<string, QueuedMessage> = new Map();
  private rateLimiter: RateLimiter;
  private processingInterval: NodeJS.Timeout | null = null;
  private isRunning = false;
  private config: Required<QueueConfig>;

  constructor(
    private client: WhatsAppClient,
    config: Partial<QueueConfig> = {}
  ) {
    super();
    
    this.config = {
      maxConcurrent: 5,
      rateLimitPerMinute: 30,
      retryAttempts: 3,
      retryDelay: 5000,
      maxQueueSize: 1000,
      priorityLevels: ['urgent', 'high', 'normal', 'low'],
      batchSize: 10,
      processingInterval: 1000,
      ...config
    };

    this.rateLimiter = new TokenBucketRateLimiter(
      this.config.rateLimitPerMinute,
      this.config.rateLimitPerMinute
    );
  }

  /**
   * Add a message to the queue
   */
  async enqueue(message: Omit<QueuedMessage, 'id' | 'retryCount' | 'createdAt'>): Promise<string> {
    if (this.queue.length >= this.config.maxQueueSize) {
      throw new Error('Queue is full');
    }

    const queuedMessage: QueuedMessage = {
      ...message,
      id: this.generateId(),
      retryCount: 0,
      maxRetries: message.maxRetries || this.config.retryAttempts,
      createdAt: new Date()
    };

    // Insert based on priority
    const priorityIndex = this.config.priorityLevels.indexOf(queuedMessage.priority);
    let insertIndex = this.queue.length;

    for (let i = 0; i < this.queue.length; i++) {
      const existingPriorityIndex = this.config.priorityLevels.indexOf(this.queue[i].priority);
      if (priorityIndex < existingPriorityIndex) {
        insertIndex = i;
        break;
      }
    }

    this.queue.splice(insertIndex, 0, queuedMessage);
    
    this.emit('message:enqueued', queuedMessage);
    this.emit('stats:updated', this.getStats());

    return queuedMessage.id;
  }

  /**
   * Add multiple messages to the queue
   */
  async enqueueBatch(messages: Omit<QueuedMessage, 'id' | 'retryCount' | 'createdAt'>[]): Promise<string[]> {
    const ids: string[] = [];
    
    for (const message of messages) {
      const id = await this.enqueue(message);
      ids.push(id);
    }

    this.emit('batch:enqueued', { count: messages.length, ids });
    return ids;
  }

  /**
   * Remove a message from the queue
   */
  dequeue(messageId: string): boolean {
    const index = this.queue.findIndex(msg => msg.id === messageId);
    if (index !== -1) {
      const message = this.queue.splice(index, 1)[0];
      this.emit('message:dequeued', message);
      this.emit('stats:updated', this.getStats());
      return true;
    }
    return false;
  }

  /**
   * Start processing the queue
   */
  start(): void {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    this.processingInterval = setInterval(() => {
      this.processQueue();
    }, this.config.processingInterval);

    this.emit('queue:started');
  }

  /**
   * Stop processing the queue
   */
  stop(): void {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }

    this.emit('queue:stopped');
  }

  /**
   * Process the queue
   */
  private async processQueue(): Promise<void> {
    if (this.processing.size >= this.config.maxConcurrent) {
      return;
    }

    const messagesToProcess = this.queue
      .filter(msg => {
        // Check if message is scheduled for future
        if (msg.scheduledAt && msg.scheduledAt > new Date()) {
          return false;
        }
        
        // Check rate limiting
        const rateLimitKey = `${msg.tel}:${msg.chatId}`;
        return this.rateLimiter.checkLimit(rateLimitKey);
      })
      .slice(0, this.config.batchSize);

    for (const message of messagesToProcess) {
      if (this.processing.size >= this.config.maxConcurrent) {
        break;
      }

      // Remove from queue and add to processing
      const index = this.queue.indexOf(message);
      this.queue.splice(index, 1);
      this.processing.set(message.id, message);

      // Process message asynchronously
      this.processMessage(message).catch(error => {
        console.error('Error processing message:', error);
      });
    }

    this.emit('stats:updated', this.getStats());
  }

  /**
   * Process a single message
   */
  private async processMessage(message: QueuedMessage): Promise<void> {
    try {
      this.emit('message:processing', message);

      // Apply rate limiting
      const rateLimitKey = `${message.tel}:${message.chatId}`;
      this.rateLimiter.addRequest(rateLimitKey);

      // Send message based on type
      let result: any;
      switch (message.type) {
        case 'text':
          result = await this.client.messages.sendText(message.tel, message.chatId, message.content.text);
          break;
        
        case 'media':
          result = await this.client.messages.sendMedia(
            message.tel,
            message.chatId,
            message.content.media,
            message.content.mimetype,
            message.content.filename
          );
          break;
        
        case 'interactive':
          result = await this.client.messages.sendButtons(
            message.tel,
            message.chatId,
            message.content.text,
            message.content.buttons
          );
          break;
        
        case 'poll':
          result = await this.client.messages.sendPoll(
            message.tel,
            message.chatId,
            message.content.poll
          );
          break;
        
        case 'location':
          result = await this.client.messages.sendLocation(
            message.tel,
            message.chatId,
            message.content.latitude,
            message.content.longitude
          );
          break;
        
        case 'voice':
          result = await this.client.messages.sendVoiceNote(
            message.tel,
            message.chatId,
            message.content.audio
          );
          break;
        
        case 'sticker':
          result = await this.client.messages.sendSticker(
            message.tel,
            message.chatId,
            message.content.sticker
          );
          break;
        
        default:
          throw new Error(`Unsupported message type: ${message.type}`);
      }

      // Mark as completed
      this.processing.delete(message.id);
      this.completed.add(message.id);
      
      this.emit('message:completed', { message, result });
      this.emit('stats:updated', this.getStats());

    } catch (error) {
      await this.handleError(message, error);
    }
  }

  /**
   * Handle message processing error
   */
  private async handleError(message: QueuedMessage, error: any): Promise<void> {
    this.processing.delete(message.id);
    message.retryCount++;

    this.emit('message:error', { message, error });

    if (message.retryCount <= message.maxRetries) {
      // Schedule retry with exponential backoff
      const delay = this.config.retryDelay * Math.pow(2, message.retryCount - 1);
      message.scheduledAt = new Date(Date.now() + delay);
      
      // Add back to queue for retry
      this.queue.unshift(message);
      
      this.emit('message:retrying', { message, delay });
    } else {
      // Mark as failed
      this.failed.set(message.id, message);
      this.emit('message:failed', { message, error });
    }

    this.emit('stats:updated', this.getStats());
  }

  /**
   * Get queue statistics
   */
  getStats(): QueueStats {
    return {
      total: this.queue.length + this.processing.size + this.completed.size + this.failed.size,
      pending: this.queue.length,
      processing: this.processing.size,
      completed: this.completed.size,
      failed: this.failed.size,
      retrying: this.queue.filter(msg => msg.retryCount > 0).length
    };
  }

  /**
   * Get message by ID
   */
  getMessage(messageId: string): QueuedMessage | null {
    return this.queue.find(msg => msg.id === messageId) ||
           this.processing.get(messageId) ||
           this.failed.get(messageId) ||
           null;
  }

  /**
   * Clear completed messages
   */
  clearCompleted(): number {
    const count = this.completed.size;
    this.completed.clear();
    this.emit('stats:updated', this.getStats());
    return count;
  }

  /**
   * Clear failed messages
   */
  clearFailed(): number {
    const count = this.failed.size;
    this.failed.clear();
    this.emit('stats:updated', this.getStats());
    return count;
  }

  /**
   * Retry all failed messages
   */
  retryFailed(): number {
    const failedMessages = Array.from(this.failed.values());
    let retriedCount = 0;

    for (const message of failedMessages) {
      if (message.retryCount < message.maxRetries) {
        message.retryCount = 0; // Reset retry count
        message.scheduledAt = undefined;
        this.queue.unshift(message);
        this.failed.delete(message.id);
        retriedCount++;
      }
    }

    this.emit('stats:updated', this.getStats());
    return retriedCount;
  }

  /**
   * Generate unique message ID
   */
  private generateId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Pause queue processing
   */
  pause(): void {
    this.stop();
    this.emit('queue:paused');
  }

  /**
   * Resume queue processing
   */
  resume(): void {
    this.start();
    this.emit('queue:resumed');
  }

  /**
   * Clear all messages from queue
   */
  clear(): void {
    const totalCleared = this.queue.length;
    this.queue.length = 0;
    this.emit('queue:cleared', { count: totalCleared });
    this.emit('stats:updated', this.getStats());
  }
}